import { Link } from "react-router-dom";
import { Quote, ArrowRight, ArrowDownUp } from "lucide-react";
import { Reveal, Overline } from "@/components/site/Reveal";
import { TESTIMONIALS, waLink } from "@/lib/data";
import { useLang } from "@/lib/i18n";

const TransformationCard = ({ t, index }) => {
    const { tr } = useLang();
    return (
        <Reveal delay={(index % 2) * 0.1} className="h-full">
            <article className="border border-white/10 bg-obsidian-surface h-full flex flex-col group" data-testid={`transformation-card-${index}`}>
                <div className="relative overflow-hidden aspect-[3/4]">
                    <img
                        src={t.photo}
                        alt={`${t.name} — Coach Roméo`}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                </div>
                <div className="p-6 md:p-8 flex flex-col flex-1">
                    <p className="flex items-center gap-2 text-ochre font-bold text-sm uppercase tracking-wider">
                        <ArrowDownUp className="h-4 w-4" /> {tr(t.result)}
                    </p>
                    <blockquote className="mt-4 text-cream/85 leading-relaxed font-light flex-1">
                        « {tr(t.quote)} »
                    </blockquote>
                    <div className="mt-6 pt-5 border-t border-white/10 flex items-center justify-between">
                        <div>
                            <p className="font-heading font-bold uppercase tracking-tight">{t.name}</p>
                            {t.location && <p className="text-xs text-sand mt-0.5">{t.location}</p>}
                        </div>
                        <Quote className="h-5 w-5 text-terracotta/60" />
                    </div>
                </div>
            </article>
        </Reveal>
    );
};

const Temoignages = () => {
    const { tr } = useLang();
    return (
        <div data-testid="temoignages-page" className="pt-28 md:pt-36">
            <section className="pb-16 md:pb-20">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Reveal>
                        <Overline>{tr("Témoignages clients", "Client testimonials")}</Overline>
                        <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.95]">
                            {tr("Leur corps a changé.", "Their body changed.")}<br />
                            <span className="font-accent italic normal-case font-medium text-ochre">
                                {tr("Leur assiette aussi.", "Their plate did too.")}
                            </span>
                        </h1>
                        <p className="mt-6 text-sand max-w-2xl leading-relaxed font-light">
                            {tr(
                                "Des parcours réels, des transformations réelles. Aucun n'a abandonné la cuisine de son enfance — tous ont appris à la maîtriser. Voici leurs photos et leurs mots.",
                                "Real journeys, real transformations. None of them gave up the food of their childhood — they all learned to master it. Here are their photos and their words."
                            )}
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="pb-24 md:pb-32" data-testid="transformations-gallery">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    {/* REAL BEFORE/AFTER */}
                    <Reveal>
                        <div className="border border-terracotta/40 bg-obsidian-surface overflow-hidden" data-testid="before-after-feature">
                            <div className="relative">
                                <img
                                    src="/images/clients/transformation-1.jpg"
                                    alt={tr("Avant et après d'un client de Coach Roméo", "Before and after of a Coach Roméo client")}
                                    className="w-full object-cover max-h-[70vh]"
                                    loading="lazy"
                                />
                                <span className="absolute top-4 left-4 rounded-full bg-black/60 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-sand">
                                    {tr("Avant", "Before")}
                                </span>
                                <span className="absolute top-4 right-4 rounded-full bg-terracotta px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-white">
                                    {tr("Après", "After")}
                                </span>
                            </div>
                            <div className="p-6 md:p-8">
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                    <p className="font-heading text-xl md:text-2xl font-bold tracking-tight">
                                        Vladimir{" "}
                                        <span className="font-accent italic font-medium text-ochre">— {tr("−22 lbs, perte de gras durable.", "22 lbs lost, lasting fat loss.")}</span>
                                    </p>
                                    <p className="text-sm text-sand shrink-0">
                                        {tr("Coaching nutrition + entraînement, par Coach Roméo.", "Nutrition + training coaching, by Coach Roméo.")}
                                    </p>
                                </div>
                                <blockquote className="mt-4 text-cream/85 leading-relaxed font-light border-l-2 border-terracotta pl-4">
                                    « {tr(
                                        "Thanks coach Roméo we are coming from so far away, God bless you 💪🙏",
                                        "Thanks coach Roméo we are coming from so far away, God bless you 💪🙏"
                                    )} »
                                </blockquote>
                            </div>
                        </div>
                    </Reveal>

                    {/* VIVIANE — AVANT/APRÈS */}
                    <Reveal className="mt-10">
                        <div className="border border-terracotta/40 bg-obsidian-surface overflow-hidden" data-testid="before-after-viviane">
                            <div className="relative">
                                <img
                                    src="/images/clients/transformation-viviane.jpg"
                                    alt={tr("Avant et après de Viviane, cliente de Coach Roméo", "Before and after of Viviane, Coach Roméo's client")}
                                    className="mx-auto w-auto max-w-full max-h-[85vh] object-contain"
                                    loading="lazy"
                                />
                                <span className="absolute top-4 left-4 rounded-full bg-black/60 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-sand">
                                    {tr("Avant", "Before")}
                                </span>
                                <span className="absolute top-4 right-4 rounded-full bg-terracotta px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-white">
                                    {tr("Après", "After")}
                                </span>
                            </div>
                            <div className="p-6 md:p-8">
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                    <p className="font-heading text-xl md:text-2xl font-bold tracking-tight">
                                        Viviane{" "}
                                        <span className="font-accent italic font-medium text-ochre">— {tr("−50 lbs en 8 mois.", "50 lbs lost in 8 months.")}</span>
                                    </p>
                                    <p className="text-sm text-sand shrink-0">
                                        {tr("Coaching nutrition + entraînement, par Coach Roméo.", "Nutrition + training coaching, by Coach Roméo.")}
                                    </p>
                                </div>
                                <blockquote className="mt-4 text-cream/85 leading-relaxed font-light border-l-2 border-terracotta pl-4">
                                    « {tr(
                                        "Roméo, merci pour tout. Je me sens plus en forme, plus légère, et je m'aime beaucoup mieux.",
                                        "Roméo, thank you for everything. I feel fitter, lighter, and I love myself so much more."
                                    )} »
                                </blockquote>
                            </div>
                        </div>
                    </Reveal>

                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                        {TESTIMONIALS.map((t, i) => (
                            <TransformationCard key={t.name} t={t} index={i} />
                        ))}
                    </div>
                </div>

                <Reveal className="mt-16">
                    <div className="max-w-7xl mx-auto px-6 md:px-12">
                        <div className="border border-terracotta/40 bg-terracotta/5 p-8 md:p-12 text-center">
                            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">
                                {tr("Le prochain témoignage,", "The next testimonial,")}{" "}
                                <span className="font-accent italic font-medium text-ochre">{tr("c'est le tien.", "could be yours.")}</span>
                            </h2>
                            <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
                                <Link
                                    to="/contact"
                                    data-testid="temoignages-cta-consultation"
                                    className="inline-flex items-center justify-center gap-3 rounded-full bg-terracotta px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors duration-300 hover:bg-terracotta-hover"
                                >
                                    {tr("Réserver ma consultation", "Book my consultation")} <ArrowRight className="h-4 w-4" />
                                </Link>
                                <a
                                    href={waLink(tr(
                                        "Bonjour Coach Roméo, les témoignages m'ont convaincu(e). Je veux commencer.",
                                        "Hello Coach Roméo, the testimonials convinced me. I want to start."
                                    ))}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="temoignages-cta-whatsapp"
                                    className="inline-flex items-center justify-center gap-3 rounded-full border border-whatsapp/60 px-8 py-4 text-sm font-bold uppercase tracking-wider text-whatsapp transition-colors duration-300 hover:bg-whatsapp hover:text-white"
                                >
                                    {tr("Écrire sur WhatsApp", "Message on WhatsApp")}
                                </a>
                            </div>
                        </div>
                    </div>
                </Reveal>
            </section>
        </div>
    );
};

export default Temoignages;
